/**
 * Matcha blogs overview — hero tabs (desktop), stacked carousels (mobile), scroll reveal.
 */
class MatchaBlogsOverview extends HTMLElement {
  #observer;
  #activePanel = null;
  #activateTab;
  #mobileQuery = window.matchMedia('(max-width: 749px)');
  #carouselCleanups = [];
  #onMobileQueryChange = () => this.#applyLayout();

  connectedCallback() {
    if (this.dataset.matchaInit === 'true') return;
    this.dataset.matchaInit = 'true';

    this.#bindTabs();
    this.#setupRevealObserver();
    this.#applyLayout();
    this.#mobileQuery.addEventListener('change', this.#onMobileQueryChange);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#clearCarousels();
    this.#mobileQuery.removeEventListener('change', this.#onMobileQueryChange);
  }

  #isMobile() {
    return this.#mobileQuery.matches;
  }

  #applyLayout() {
    if (this.#isMobile()) {
      this.#enableMobileLayout();
      return;
    }

    this.#enableDesktopLayout();
  }

  #enableMobileLayout() {
    this.#clearCarousels();

    this.querySelectorAll('[data-blog-panel]').forEach((panel) => {
      panel.hidden = false;
      panel.classList.add('is-active');
      panel.querySelectorAll('[data-matcha-reveal]').forEach((el) => el.classList.add('is-revealed'));

      const cleanup = this.#initCarousel(panel);
      if (cleanup) this.#carouselCleanups.push(cleanup);
    });

    const empty = this.querySelector('[data-blog-empty]');
    if (empty) empty.hidden = true;
  }

  #enableDesktopLayout() {
    this.#clearCarousels();

    const defaultTab =
      this.querySelector('[data-blog-tab].is-active') ||
      this.querySelector('[data-blog-tab="matcha"]') ||
      this.querySelector('[data-blog-tab]');

    if (defaultTab && this.#activateTab) {
      this.#activateTab(defaultTab, { scroll: false });
    }
  }

  #clearCarousels() {
    this.#carouselCleanups.forEach((cleanup) => cleanup());
    this.#carouselCleanups = [];
  }

  #initCarousel(panel) {
    const track = panel.querySelector('[data-carousel-track]');
    const nav = panel.querySelector('[data-blog-carousel-nav]');
    const prev = panel.querySelector('[data-carousel-prev]');
    const next = panel.querySelector('[data-carousel-next]');

    if (!track || !prev || !next) {
      nav?.setAttribute('hidden', '');
      return null;
    }

    const slides = track.querySelectorAll('[data-matcha-reveal]');
    if (slides.length <= 1) {
      nav?.setAttribute('hidden', '');
      return null;
    }

    nav?.removeAttribute('hidden');

    const updateButtons = () => {
      const atStart = track.scrollLeft <= 2;
      const atEnd = track.scrollLeft + track.clientWidth >= track.scrollWidth - 2;
      prev.toggleAttribute('disabled', atStart);
      next.toggleAttribute('disabled', atEnd);
    };

    const scrollBySlide = (direction) => {
      const slide = track.querySelector('[data-matcha-reveal]');
      const gap = Number.parseFloat(getComputedStyle(track).columnGap || getComputedStyle(track).gap) || 16;
      const amount = slide ? slide.offsetWidth + gap : track.clientWidth * 0.85;
      track.scrollBy({
        left: direction * amount,
        behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth',
      });
    };

    const onPrev = () => scrollBySlide(-1);
    const onNext = () => scrollBySlide(1);

    prev.addEventListener('click', onPrev);
    next.addEventListener('click', onNext);
    track.addEventListener('scroll', updateButtons, { passive: true });
    window.addEventListener('resize', updateButtons, { passive: true });

    updateButtons();

    return () => {
      prev.removeEventListener('click', onPrev);
      next.removeEventListener('click', onNext);
      track.removeEventListener('scroll', updateButtons);
      window.removeEventListener('resize', updateButtons);
    };
  }

  #bindTabs() {
    const tabs = this.querySelectorAll('[data-blog-tab]');
    const panels = this.querySelectorAll('[data-blog-panel]');
    const empty = this.querySelector('[data-blog-empty]');

    if (!tabs.length || !panels.length) return;

    const activate = (tab, { scroll = true } = {}) => {
      if (this.#isMobile()) return;

      const panelId = tab.dataset.blogTab;
      if (!panelId) return;

      tabs.forEach((button) => {
        const isActive = button === tab;
        button.classList.toggle('is-active', isActive);
        button.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      panels.forEach((panel) => {
        const isActive = panel.dataset.blogPanel === panelId;
        panel.hidden = !isActive;
        panel.classList.toggle('is-active', isActive);

        if (isActive) {
          this.#activePanel = panel;
          this.#revealPanel(panel);
        }
      });

      if (empty) {
        empty.hidden = true;
      }

      if (scroll) {
        const panel = this.querySelector(`[data-blog-panel="${panelId}"]`);
        panel?.scrollIntoView({ behavior: this.#prefersReducedMotion() ? 'auto' : 'smooth', block: 'nearest' });
      }
    };

    tabs.forEach((tab) => {
      tab.addEventListener('click', () => activate(tab));
    });

    this.#activateTab = activate;
  }

  #revealPanel(panel) {
    if (!panel || this.#prefersReducedMotion()) {
      panel?.querySelectorAll('[data-matcha-reveal]').forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    panel.querySelectorAll('[data-matcha-reveal]').forEach((el, index) => {
      el.classList.remove('is-revealed');
      el.style.setProperty('--hub-reveal-index', String(index % 6));
      window.requestAnimationFrame(() => {
        window.setTimeout(() => el.classList.add('is-revealed'), 40);
      });
    });

    if (this.#observer) {
      panel.querySelectorAll('[data-matcha-reveal]:not(.is-revealed)').forEach((el) => {
        this.#observer.observe(el);
      });
    }
  }

  #setupRevealObserver() {
    const targets = this.querySelectorAll('[data-matcha-reveal]');
    if (!targets.length) return;

    if (this.#prefersReducedMotion()) {
      targets.forEach((el) => el.classList.add('is-revealed'));
      return;
    }

    this.#observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-revealed');
          this.#observer?.unobserve(entry.target);
        });
      },
      { root: null, rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );

    this.querySelectorAll('.matcha-blogs-overview__filters[data-matcha-reveal]').forEach((el) => {
      this.#observer.observe(el);
    });

    if (!this.#isMobile()) {
      const defaultPanel = this.querySelector('[data-blog-panel]:not([hidden])');
      defaultPanel?.querySelectorAll('[data-matcha-reveal]:not(.is-revealed)').forEach((el) => {
        this.#observer.observe(el);
      });
    }
  }

  #prefersReducedMotion() {
    return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  }
}

if (!customElements.get('matcha-blogs-overview')) {
  customElements.define('matcha-blogs-overview', MatchaBlogsOverview);
}
